/**
 * AdminClient - typed HTTP client for tilt-admin-api.
 *
 * Constructed with a base URL (derived per env: `https://api.{env}.apps.myfinterra.com`
 * or, for local dev, the execute-api.* URL from the tilt-admin-api-dev stack
 * output) and a TokenStore that knows how to produce the current JWT.
 *
 * Every request injects `Authorization: Bearer <access_token>` except the
 * explicit `health()` method, which hits the un-authed /health route.
 *
 * Non-2xx responses raise `ApiError` with the parsed JSON body + status.
 * 401 additionally fires `tokens.onUnauthorized?.()` so the host app can
 * redirect to the login screen.
 */
import { ApiError } from "./types.js";
/**
 * Append shared ``limit``/``offset``/``q`` query-string params when
 * the caller supplied them. Kept as a module-local helper so every
 * paginated list method shares the exact same serialisation rules -
 * blank ``q``/``0`` values get sent verbatim, which matches what
 * ``tilt_admin_api.pagination.parse_params`` expects.
 */
function appendPagination(qs, params) {
    if (!params)
        return;
    if (params.limit != null)
        qs.set("limit", String(params.limit));
    if (params.offset != null)
        qs.set("offset", String(params.offset));
    if (params.q != null && params.q !== "")
        qs.set("q", params.q);
    if (params.scope_path != null && params.scope_path !== "") {
        qs.set("scope_path", params.scope_path);
    }
}
function paginationSuffix(params) {
    const qs = new URLSearchParams();
    appendPagination(qs, params);
    return qs.toString() ? `?${qs.toString()}` : "";
}
export class AdminClient {
    baseUrl;
    tokens;
    fetchImpl;
    constructor(opts) {
        this.baseUrl = opts.baseUrl.replace(/\/$/, "");
        this.tokens = opts.tokens;
        // Bind the built-in fetch to the global scope. Storing the reference
        // as a field detaches `this` from Window, and browsers then throw
        // "Illegal invocation" on call. Explicit .bind(globalThis) fixes it
        // and works in both browser + node test environments.
        this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
    }
    /** GET /health - un-authenticated. */
    async health() {
        return this.request("GET", "/health", { authenticated: false });
    }
    /** GET /admin/v1/profile - returns the ACTOR's identity + roles.
     *  Never swaps during impersonation (by design - audit attribution
     *  reads from here). Use ``getActingAsProfile`` for UI role-view
     *  decisions, or impersonation won't visually swap. */
    async getProfile() {
        return this.request("GET", "/admin/v1/profile");
    }
    /** GET /admin/v1/profile/acting-as - returns the TARGET's identity
     *  + roles when an impersonation session is active, or the actor's
     *  identity verbatim when not. Response adds ``impersonating``,
     *  ``session_id`` and ``actor`` fields on top of the Profile shape.
     *  Sidebar items, action-button gates, and every role-aware
     *  predicate should read from this endpoint. */
    async getActingAsProfile() {
        return this.request("GET", "/admin/v1/profile/acting-as");
    }
    /** GET /admin/v1/impersonation/current - the caller's active session, if any. */
    async getCurrentImpersonation() {
        return this.request("GET", "/admin/v1/impersonation/current");
    }
    /** POST /admin/v1/impersonation - start a new session (super_admin only). */
    async startImpersonation(body) {
        return this.request("POST", "/admin/v1/impersonation", { body });
    }
    /** POST /admin/v1/impersonation/{id}/stop - actor exits. */
    async stopImpersonation(id) {
        return this.request("POST", `/admin/v1/impersonation/${encodeURIComponent(id)}/stop`);
    }
    /** GET /admin/v1/impersonation - list (super: all, else: own-target). */
    async listImpersonations() {
        return this.request("GET", "/admin/v1/impersonation");
    }
    /** POST /admin/v1/impersonation/{id}/kill - super_admin force-ends. */
    async killImpersonation(id) {
        return this.request("POST", `/admin/v1/impersonation/${encodeURIComponent(id)}/kill`);
    }
    /** GET /admin/v1/users - scoped to the caller's subtree. */
    async listUsers(params) {
        return this.request("GET", `/admin/v1/users${paginationSuffix(params)}`);
    }
    /** POST /admin/v1/users - invite. */
    async inviteUser(body) {
        return this.request("POST", "/admin/v1/users", { body });
    }
    /** GET /admin/v1/users/{id} - detail. */
    async getUser(id) {
        return this.request("GET", `/admin/v1/users/${encodeURIComponent(id)}`);
    }
    /** PATCH /admin/v1/users/{id} - update first/last name, primary partner. */
    async patchUser(id, body) {
        return this.request("PATCH", `/admin/v1/users/${encodeURIComponent(id)}`, { body });
    }
    /** POST /admin/v1/users/{id}/disable. */
    async disableUser(id) {
        return this.request("POST", `/admin/v1/users/${encodeURIComponent(id)}/disable`);
    }
    /** POST /admin/v1/users/{id}/enable. */
    async enableUser(id) {
        return this.request("POST", `/admin/v1/users/${encodeURIComponent(id)}/enable`);
    }
    /**
     * POST /admin/v1/users/{id}/resend-invite - re-issue the Cognito
     * invite email. 409 if the user already accepted (operator should
     * use password-reset instead); 404 if no Cognito record exists.
     */
    async resendUserInvite(id) {
        return this.request("POST", `/admin/v1/users/${encodeURIComponent(id)}/resend-invite`);
    }
    /**
     * POST /admin/v1/users/{id}/remove-from-hierarchy - partner-scoped
     * "disable". Strips every role membership the user holds inside the
     * caller's partner-scope write subtree. The user's Cognito record
     * is NOT touched - they may still belong to other partner trees.
     * Response includes `removed_count` alongside the refreshed user.
     */
    async removeUserFromHierarchy(id) {
        return this.request("POST", `/admin/v1/users/${encodeURIComponent(id)}/remove-from-hierarchy`);
    }
    /** POST /admin/v1/users/{id}/roles - grant. */
    async grantUserRole(id, partner_id, role) {
        return this.request("POST", `/admin/v1/users/${encodeURIComponent(id)}/roles`, {
            body: { partner_id, role },
        });
    }
    /** DELETE /admin/v1/users/{id}/roles/{partner_id}/{role} - revoke. */
    async revokeUserRole(id, partner_id, role) {
        return this.request("DELETE", `/admin/v1/users/${encodeURIComponent(id)}/roles/${encodeURIComponent(partner_id)}/${encodeURIComponent(role)}`);
    }
    // ----- V0024 multi-scope memberships ----- //
    async listUserMemberships(userId) {
        return this.request("GET", `/admin/v1/users/${encodeURIComponent(userId)}/memberships`);
    }
    async createUserMembership(userId, body) {
        return this.request("POST", `/admin/v1/users/${encodeURIComponent(userId)}/memberships`, {
            body,
        });
    }
    async updateUserMembership(userId, membershipId, body) {
        return this.request("PATCH", `/admin/v1/users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(membershipId)}`, { body });
    }
    async deleteUserMembership(userId, membershipId) {
        return this.request("DELETE", `/admin/v1/users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(membershipId)}`);
    }
    async listMembershipDepartments(userId, membershipId) {
        return this.request("GET", `/admin/v1/users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(membershipId)}/departments`);
    }
    async putMembershipDepartments(userId, membershipId, departmentIds) {
        return this.request("PUT", `/admin/v1/users/${encodeURIComponent(userId)}/memberships/${encodeURIComponent(membershipId)}/departments`, { body: { department_ids: departmentIds } });
    }
    /** GET /admin/v1/partners - scoped to caller's subtree. */
    async listPartners(params) {
        const qs = new URLSearchParams();
        if (params?.parent_id)
            qs.set("parent_id", params.parent_id);
        if (params?.partner_type)
            qs.set("partner_type", params.partner_type);
        if (params?.include_deleted)
            qs.set("include_deleted", "true");
        appendPagination(qs, params);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/partners${suffix}`);
    }
    /** POST /admin/v1/partners - create child partner. */
    async createPartner(body) {
        return this.request("POST", "/admin/v1/partners", { body });
    }
    /** GET /admin/v1/partners/{id}. */
    async getPartner(id) {
        return this.request("GET", `/admin/v1/partners/${encodeURIComponent(id)}`);
    }
    /** PATCH /admin/v1/partners/{id}. */
    async patchPartner(id, body) {
        return this.request("PATCH", `/admin/v1/partners/${encodeURIComponent(id)}`, { body });
    }
    /** DELETE /admin/v1/partners/{id} - soft-delete (blocks on descendants). */
    async deletePartner(id) {
        return this.request("DELETE", `/admin/v1/partners/${encodeURIComponent(id)}`);
    }
    /**
     * DELETE /admin/v1/partners/{id}/cascade-hard - physically removes
     * the partner and every dependent row (orgs, locations, depts,
     * provider creds/bindings/access-log, user memberships). Frees the
     * globally-unique partner slug so a failed Quick Onboard can be
     * retried with the same name. Super_admin only.
     */
    async cascadeHardDeletePartner(id) {
        return this.request("DELETE", `/admin/v1/partners/${encodeURIComponent(id)}/cascade-hard`);
    }
    /** GET /admin/v1/organizations - caller-scoped, optional partner filter. */
    async listOrganizations(params) {
        const qs = new URLSearchParams();
        if (params?.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params?.include_deleted)
            qs.set("include_deleted", "true");
        appendPagination(qs, params);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/organizations${suffix}`);
    }
    /** POST /admin/v1/organizations. */
    async createOrganization(body) {
        return this.request("POST", "/admin/v1/organizations", { body });
    }
    /** GET /admin/v1/organizations/{id}. */
    async getOrganization(id) {
        return this.request("GET", `/admin/v1/organizations/${encodeURIComponent(id)}`);
    }
    /** PATCH /admin/v1/organizations/{id}. */
    async patchOrganization(id, body) {
        return this.request("PATCH", `/admin/v1/organizations/${encodeURIComponent(id)}`, { body });
    }
    /** DELETE /admin/v1/organizations/{id} - soft-delete (blocks with live locations). */
    async deleteOrganization(id) {
        return this.request("DELETE", `/admin/v1/organizations/${encodeURIComponent(id)}`);
    }
    /** GET /admin/v1/locations - caller-scoped. */
    async listLocations(params) {
        const qs = new URLSearchParams();
        if (params?.organization_id)
            qs.set("organization_id", params.organization_id);
        if (params?.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params?.status)
            qs.set("status", params.status);
        if (params?.include_deleted)
            qs.set("include_deleted", "true");
        appendPagination(qs, params);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/locations${suffix}`);
    }
    /** POST /admin/v1/locations. */
    async createLocation(body) {
        return this.request("POST", "/admin/v1/locations", { body });
    }
    /** GET /admin/v1/locations/{id}. */
    async getLocation(id) {
        return this.request("GET", `/admin/v1/locations/${encodeURIComponent(id)}`);
    }
    /** PATCH /admin/v1/locations/{id}. */
    async patchLocation(id, body) {
        return this.request("PATCH", `/admin/v1/locations/${encodeURIComponent(id)}`, {
            body,
        });
    }
    /** DELETE /admin/v1/locations/{id} - soft-delete, cascades into departments. */
    async deleteLocation(id) {
        return this.request("DELETE", `/admin/v1/locations/${encodeURIComponent(id)}`);
    }
    /**
     * PUT /admin/v1/locations/{id}/kiosk-pin
     * Set or clear the per-location kiosk admin PIN.
     * Pass `pin` to set (4–8 chars); pass `null` to clear.
     * Returns 204 No Content on success.
     */
    async setLocationKioskPin(id, pin) {
        return this.request("PUT", `/admin/v1/locations/${encodeURIComponent(id)}/kiosk-pin`, {
            body: { pin },
        });
    }
    // ----------------------------------------------------------------- //
    // Integration Health (V0072 / #226) - vendor-error surface.
    // ----------------------------------------------------------------- //
    /** GET /admin/v1/integration-health - vendor-error events. The
     *  response shape is a discriminated union - pass ``group:
     *  "fingerprint"`` to collapse identical errors (response carries
     *  ``groups`` instead of ``events``). */
    async listIntegrationHealthEvents(params = {}) {
        const qs = new URLSearchParams();
        if (params.provider_slug)
            qs.set("provider_slug", params.provider_slug);
        if (params.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params.feature_path)
            qs.set("feature_path", params.feature_path);
        if (params.include_acknowledged)
            qs.set("include_acknowledged", "true");
        if (params.since)
            qs.set("since", params.since);
        if (params.until)
            qs.set("until", params.until);
        if (params.group)
            qs.set("group", params.group);
        if (params.limit != null)
            qs.set("limit", String(params.limit));
        if (params.offset != null)
            qs.set("offset", String(params.offset));
        const query = qs.toString();
        return this.request("GET", `/admin/v1/integration-health${query ? `?${query}` : ""}`);
    }
    /** POST /admin/v1/integration-health/events/{id}/acknowledge */
    async acknowledgeIntegrationHealthEvent(id) {
        return this.request("POST", `/admin/v1/integration-health/events/${encodeURIComponent(id)}/acknowledge`);
    }
    /** POST /admin/v1/integration-health/events/acknowledge - bulk.
     *  One round-trip flips up to 500 events. Avoids the per-row
     *  503-throttling the older parallel-loop approach hit. Returns
     *  per-state counts; ``forbidden_or_not_found`` covers both
     *  visibility-failed and not-found IDs (the API doesn't disclose
     *  which is which to keep the "is this id real?" probe blocked). */
    async bulkAcknowledgeIntegrationHealthEvents(ids) {
        return this.request("POST", "/admin/v1/integration-health/events/acknowledge", {
            body: { ids },
        });
    }
    /** GET /admin/v1/integration-health/delegations - super_admin only. */
    async listIntegrationHealthDelegations() {
        return this.request("GET", "/admin/v1/integration-health/delegations");
    }
    /** POST /admin/v1/integration-health/delegations - super_admin only. */
    async grantIntegrationHealthDelegation(body) {
        return this.request("POST", "/admin/v1/integration-health/delegations", { body });
    }
    /** DELETE /admin/v1/integration-health/delegations/{id} - super_admin only. */
    async revokeIntegrationHealthDelegation(id) {
        return this.request("DELETE", `/admin/v1/integration-health/delegations/${encodeURIComponent(id)}`);
    }
    // ----------------------------------------------------------------- //
    // Roles + permissions catalog (Phase A - read-only).
    // ----------------------------------------------------------------- //
    /** GET /admin/v1/permissions - global permission catalog. */
    async listPermissions() {
        return this.request("GET", "/admin/v1/permissions");
    }
    /** GET /admin/v1/roles - system + partner-subtree roles. */
    async listRoles(params = {}) {
        const qs = new URLSearchParams();
        if (params.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params.include_system === false)
            qs.set("include_system", "false");
        if (params.include_deleted)
            qs.set("include_deleted", "true");
        const query = qs.toString();
        return this.request("GET", `/admin/v1/roles${query ? `?${query}` : ""}`);
    }
    /** GET /admin/v1/roles/{id} - single role with permission grants. */
    async getRole(id) {
        return this.request("GET", `/admin/v1/roles/${encodeURIComponent(id)}`);
    }
    /** POST /admin/v1/roles - create a custom partner-scoped role. */
    async createRole(body) {
        return this.request("POST", "/admin/v1/roles", { body });
    }
    /** PATCH /admin/v1/roles/{id} - edit a custom role. */
    async patchRole(id, body) {
        return this.request("PATCH", `/admin/v1/roles/${encodeURIComponent(id)}`, {
            body,
        });
    }
    /** DELETE /admin/v1/roles/{id} - soft-delete a custom role. */
    async deleteRole(id) {
        return this.request("DELETE", `/admin/v1/roles/${encodeURIComponent(id)}`);
    }
    /**
     * GET /admin/v1/locations/{id}/inventory-cache - list every cached
     * provider snapshot for the location (one row per provider_slug).
     */
    async listLocationInventoryCache(id) {
        return this.request("GET", `/admin/v1/locations/${encodeURIComponent(id)}/inventory-cache`);
    }
    /**
     * DELETE /admin/v1/locations/{id}/inventory-cache - wipe inventory
     * snapshots for a single location. Pass ``{providerSlug: "marketcheck"}``
     * to clear just that provider; omit to clear every provider for the
     * location. Best-effort S3 delete + row delete from
     * inventory_cache_state_tbl. Idempotent.
     */
    async clearLocationInventoryCache(id, options) {
        const qs = options?.providerSlug
            ? `?provider_slug=${encodeURIComponent(options.providerSlug)}`
            : "";
        return this.request("DELETE", `/admin/v1/locations/${encodeURIComponent(id)}/inventory-cache${qs}`);
    }
    // ----------------------------------------------------------------- //
    // Provider credentials - Admin-1a-13 /admin/v1/provider-credentials.
    // ----------------------------------------------------------------- //
    /** GET /admin/v1/provider-credentials - caller-scoped; masked preview only. */
    async listProviderCredentials(params) {
        const qs = new URLSearchParams();
        if (params?.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params?.organization_id)
            qs.set("organization_id", params.organization_id);
        if (params?.location_id)
            qs.set("location_id", params.location_id);
        if (params?.provider)
            qs.set("provider", params.provider);
        if (params?.environment)
            qs.set("environment", params.environment);
        if (params?.include_deleted)
            qs.set("include_deleted", "true");
        appendPagination(qs, params);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/provider-credentials${suffix}`);
    }
    /** POST /admin/v1/provider-credentials - create with envelope encrypt + audit. */
    async createProviderCredential(body) {
        return this.request("POST", "/admin/v1/provider-credentials", { body });
    }
    /** GET /admin/v1/provider-credentials/{id} - metadata only. */
    async getProviderCredential(id) {
        return this.request("GET", `/admin/v1/provider-credentials/${encodeURIComponent(id)}`);
    }
    /**
     * GET /admin/v1/provider-credentials/{id}/masked - returns masked_summary
     * and writes an `action='reveal_masked'` audit row. Reason is required
     * (enforced server-side) and passed as a query param.
     */
    async revealMaskedCredential(id, reason) {
        const qs = new URLSearchParams({ reason });
        return this.request("GET", `/admin/v1/provider-credentials/${encodeURIComponent(id)}/masked?${qs.toString()}`);
    }
    /**
     * GET /admin/v1/provider-credentials/{id}/bindings - the reverse
     * index: bindings that pin this credential directly. Used by the
     * Credentials page "Bindings using this credential" panel + the
     * rotate flow's impact preview.
     */
    async listCredentialBindings(id) {
        return this.request("GET", `/admin/v1/provider-credentials/${encodeURIComponent(id)}/bindings`);
    }
    /** POST /admin/v1/provider-credentials/{id}/rotate - soft-delete old + insert new. */
    async rotateProviderCredential(id, body) {
        return this.request("POST", `/admin/v1/provider-credentials/${encodeURIComponent(id)}/rotate`, { body });
    }
    /** DELETE /admin/v1/provider-credentials/{id} - soft-delete; reason required. */
    async deleteProviderCredential(id, reason) {
        return this.request("DELETE", `/admin/v1/provider-credentials/${encodeURIComponent(id)}`, { body: { reason } });
    }
    /** GET /admin/v1/audit - scoped to caller's subtree + keyset-paginated. */
    async listAudit(params) {
        const qs = new URLSearchParams();
        if (params?.action)
            qs.set("action", params.action);
        if (params?.resource_type)
            qs.set("resource_type", params.resource_type);
        if (params?.resource_id)
            qs.set("resource_id", params.resource_id);
        if (params?.actor_user_id)
            qs.set("actor_user_id", params.actor_user_id);
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        if (params?.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params?.cursor)
            qs.set("cursor", params.cursor);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/audit${suffix}`);
    }
    // ---------------------------------------------------------------- //
    // Platform catalogs (super_admin only)
    // ---------------------------------------------------------------- //
    async listVerticals() {
        return this.request("GET", "/admin/v1/platform/verticals");
    }
    async getVertical(id) {
        return this.request("GET", `/admin/v1/platform/verticals/${encodeURIComponent(id)}`);
    }
    async createVertical(body) {
        return this.request("POST", "/admin/v1/platform/verticals", { body });
    }
    async patchVertical(id, body) {
        return this.request("PATCH", `/admin/v1/platform/verticals/${encodeURIComponent(id)}`, {
            body,
        });
    }
    async deleteVertical(id) {
        return this.request("DELETE", `/admin/v1/platform/verticals/${encodeURIComponent(id)}`);
    }
    async createVerticalDepartment(verticalId, body) {
        return this.request("POST", `/admin/v1/platform/verticals/${encodeURIComponent(verticalId)}/departments`, { body });
    }
    async deleteVerticalDepartment(verticalId, deptId) {
        return this.request("DELETE", `/admin/v1/platform/verticals/${encodeURIComponent(verticalId)}/departments/${encodeURIComponent(deptId)}`);
    }
    /**
     * Preflight for the "propagate to curated orgs / locations" prompt
     * (#154). Returns counts so the UI can render
     * "Add this dept to N orgs + M locations? (P uncurated orgs will
     * keep inheriting from the catalog merge)".
     */
    async getDepartmentPropagationImpact(verticalId, deptId) {
        return this.request("GET", `/admin/v1/platform/verticals/${encodeURIComponent(verticalId)}/departments/${encodeURIComponent(deptId)}/propagation-impact`);
    }
    /**
     * Bulk-insert this dept into curated org and / or location subsets.
     * Idempotent - ON CONFLICT DO NOTHING server-side; uncurated rows
     * are skipped (they fall through to the catalog merge already).
     */
    async propagateDepartment(verticalId, deptId, body) {
        return this.request("POST", `/admin/v1/platform/verticals/${encodeURIComponent(verticalId)}/departments/${encodeURIComponent(deptId)}/propagate`, { body });
    }
    // -------- Configuration Settings Catalog (#168) -------- //
    async listPaymentMethodCatalog() {
        return this.request("GET", "/admin/v1/platform/payment-methods");
    }
    async updatePaymentMethod(featurePath, body) {
        return this.request("PUT", `/admin/v1/platform/payment-methods/${encodeURIComponent(featurePath)}`, { body });
    }
    async resetPaymentMethodCatalogDefaults() {
        return this.request("POST", "/admin/v1/platform/payment-methods/reset-defaults");
    }
    async listProviderFlowOverrides() {
        return this.request("GET", "/admin/v1/platform/provider-flow-overrides");
    }
    async upsertProviderFlowOverride(body) {
        return this.request("PUT", "/admin/v1/platform/provider-flow-overrides", { body });
    }
    async deleteProviderFlowOverride(providerSlug, featurePath) {
        return this.request("DELETE", `/admin/v1/platform/provider-flow-overrides/${encodeURIComponent(providerSlug)}/${encodeURIComponent(featurePath)}`);
    }
    async listConfigurationSettings() {
        return this.request("GET", "/admin/v1/platform/configuration-settings");
    }
    async createConfigurationSetting(body) {
        return this.request("POST", "/admin/v1/platform/configuration-settings", { body });
    }
    async patchConfigurationSetting(id, body) {
        return this.request("PATCH", `/admin/v1/platform/configuration-settings/${encodeURIComponent(id)}`, { body });
    }
    async deleteConfigurationSetting(id) {
        return this.request("DELETE", `/admin/v1/platform/configuration-settings/${encodeURIComponent(id)}`);
    }
    async listProviderCatalog(params) {
        const qs = new URLSearchParams();
        if (params?.category)
            qs.set("category", params.category);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/providers${suffix}`);
    }
    async getProviderCatalogEntry(id) {
        return this.request("GET", `/admin/v1/platform/providers/${encodeURIComponent(id)}`);
    }
    async createProviderCatalogEntry(body) {
        return this.request("POST", "/admin/v1/platform/providers", { body });
    }
    async patchProviderCatalogEntry(id, body) {
        return this.request("PATCH", `/admin/v1/platform/providers/${encodeURIComponent(id)}`, {
            body,
        });
    }
    async deleteProviderCatalogEntry(id) {
        return this.request("DELETE", `/admin/v1/platform/providers/${encodeURIComponent(id)}`);
    }
    async listProviderFeatures(id) {
        return this.request("GET", `/admin/v1/platform/providers/${encodeURIComponent(id)}/features`);
    }
    async addProviderFeature(id, body) {
        return this.request("POST", `/admin/v1/platform/providers/${encodeURIComponent(id)}/features`, {
            body,
        });
    }
    async removeProviderFeature(id, featurePath) {
        return this.request("DELETE", `/admin/v1/platform/providers/${encodeURIComponent(id)}/features/${encodeURIComponent(featurePath)}`);
    }
    /** V0063 - flip rollout_policy on a (provider, feature) catalog row. */
    async patchProviderFeatureRollout(id, featurePath, body) {
        return this.request("PATCH", `/admin/v1/platform/providers/${encodeURIComponent(id)}/features/${encodeURIComponent(featurePath)}`, { body });
    }
    /** V0063 - flip rollout_policy on a feature_tbl row (whole feature). */
    async patchFeatureRollout(featurePath, body) {
        return this.request("PATCH", `/admin/v1/platform/features/${encodeURIComponent(featurePath)}`, {
            body,
        });
    }
    // ---------------------------------------------------------------- //
    // Provider bindings
    // ---------------------------------------------------------------- //
    async listProviderBindings(params) {
        const qs = new URLSearchParams();
        if (params.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params.organization_id)
            qs.set("organization_id", params.organization_id);
        if (params.location_id)
            qs.set("location_id", params.location_id);
        return this.request("GET", `/admin/v1/provider-bindings?${qs.toString()}`);
    }
    async getResolvedBindings(params) {
        const qs = new URLSearchParams();
        if (params.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params.organization_id)
            qs.set("organization_id", params.organization_id);
        if (params.location_id)
            qs.set("location_id", params.location_id);
        return this.request("GET", `/admin/v1/provider-bindings/resolved?${qs.toString()}`);
    }
    async createProviderBinding(body) {
        return this.request("POST", "/admin/v1/provider-bindings", { body });
    }
    /**
     * Push 13: one-shot batched write that creates/updates all scope rows
     * for a single (provider, type, subtype) tuple from one flat config.
     */
    async applyProviderBinding(body) {
        return this.request("POST", "/admin/v1/provider-bindings/apply", { body });
    }
    async patchProviderBinding(id, body) {
        return this.request("PATCH", `/admin/v1/provider-bindings/${encodeURIComponent(id)}`, {
            body,
        });
    }
    async deleteProviderBinding(id) {
        return this.request("DELETE", `/admin/v1/provider-bindings/${encodeURIComponent(id)}`);
    }
    // ----------------------------------------------------------------- //
    // Feature catalog + per-scope on/off cascade (V0060).                //
    // ----------------------------------------------------------------- //
    async listFeatureCatalog() {
        return this.request("GET", "/admin/v1/platform/features");
    }
    async listScopeFeatureEntitlements(scopeKind, scopeId) {
        return this.request("GET", `/admin/v1/${scopeKind}/${encodeURIComponent(scopeId)}/feature-entitlements`);
    }
    async setFeatureEntitlement(scopeKind, scopeId, featurePath, isEnabled) {
        return this.request("PUT", `/admin/v1/${scopeKind}/${encodeURIComponent(scopeId)}/feature-entitlements/${encodeURIComponent(featurePath)}`, { body: { is_enabled: isEnabled } });
    }
    async clearFeatureEntitlement(scopeKind, scopeId, featurePath) {
        return this.request("DELETE", `/admin/v1/${scopeKind}/${encodeURIComponent(scopeId)}/feature-entitlements/${encodeURIComponent(featurePath)}`);
    }
    // ----------------------------------------------------------------- //
    // V0060 - feature × provider allowlist (per-scope sparse rows).      //
    // ----------------------------------------------------------------- //
    async listScopeFeatureProviders(scopeKind, scopeId) {
        return this.request("GET", `/admin/v1/${scopeKind}/${encodeURIComponent(scopeId)}/feature-providers`);
    }
    async setFeatureProviderAllowlist(scopeKind, scopeId, featurePath, providerSlug, isAllowed) {
        return this.request("PUT", `/admin/v1/${scopeKind}/${encodeURIComponent(scopeId)}/feature-providers/${encodeURIComponent(featurePath)}/${encodeURIComponent(providerSlug)}`, { body: { is_allowed: isAllowed } });
    }
    async clearFeatureProviderAllowlist(scopeKind, scopeId, featurePath, providerSlug) {
        return this.request("DELETE", `/admin/v1/${scopeKind}/${encodeURIComponent(scopeId)}/feature-providers/${encodeURIComponent(featurePath)}/${encodeURIComponent(providerSlug)}`);
    }
    async listProviderDevices(bindingId) {
        return this.request("GET", `/admin/v1/provider-bindings/${encodeURIComponent(bindingId)}/devices`);
    }
    async createProviderDevice(bindingId, body) {
        return this.request("POST", `/admin/v1/provider-bindings/${encodeURIComponent(bindingId)}/devices`, { body });
    }
    async patchProviderDevice(bindingId, deviceId, body) {
        return this.request("PATCH", `/admin/v1/provider-bindings/${encodeURIComponent(bindingId)}/devices/${encodeURIComponent(deviceId)}`, { body });
    }
    async deleteProviderDevice(bindingId, deviceId) {
        return this.request("DELETE", `/admin/v1/provider-bindings/${encodeURIComponent(bindingId)}/devices/${encodeURIComponent(deviceId)}`);
    }
    async seedLocationDepartments(locationId) {
        return this.request("POST", `/admin/v1/locations/${encodeURIComponent(locationId)}/seed-departments`);
    }
    /**
     * Narrow GET for enum sources declared by `ProviderFieldSchema`.
     * Caller passes the relative path (already with :placeholders
     * substituted); returns the enum-options envelope admin-api
     * passthrough routes emit - `{options: [{value, label}]}`.
     */
    async fetchEnumOptions(path) {
        return this.request("GET", path);
    }
    /**
     * POST /admin/v1/uploads/brand-logo - ask admin-api for a presigned
     * PUT URL + the final public URL to save in brand_config.logo_url.
     */
    async requestBrandLogoUpload(body) {
        return this.request("POST", "/admin/v1/uploads/brand-logo", { body });
    }
    /**
     * POST /admin/v1/uploads/payment-method-icon - ask admin-api for a
     * presigned PUT URL for a custom payment method icon (platform-global).
     * Returns the public URL to save in icon_light_url or icon_dark_url.
     * Super-admin only.
     */
    async requestPaymentMethodIconUpload(body) {
        return this.request("POST", "/admin/v1/uploads/payment-method-icon", { body });
    }
    /**
     * POST /admin/v1/provider-bindings/{id}/sync-devices - Valor (and
     * future providers) bulk-import devices from the upstream API into
     * provider_device_tbl. Returns the list of synced devices.
     */
    async syncProviderBindingDevices(bindingId) {
        return this.request("POST", `/admin/v1/provider-bindings/${encodeURIComponent(bindingId)}/sync-devices`);
    }
    async request(method, path, opts = {}) {
        const { authenticated = true, body } = opts;
        const headers = {
            "content-type": "application/json",
        };
        if (authenticated) {
            const token = await this.tokens.getAccessToken();
            if (!token) {
                await this.tokens.onUnauthorized?.();
                throw new ApiError(401, { error: "unauthenticated" });
            }
            headers.authorization = `Bearer ${token}`;
        }
        // exactOptionalPropertyTypes treats `body: undefined` as different from
        // omitting the key entirely. Build the init lazily and conditionally
        // include `body` only when we actually have one.
        const init = { method, headers };
        if (body !== undefined) {
            init.body = JSON.stringify(body);
        }
        const response = await this.fetchImpl(`${this.baseUrl}${path}`, init);
        const text = await response.text();
        let parsed;
        try {
            parsed = text.length > 0 ? JSON.parse(text) : undefined;
        }
        catch {
            parsed = text;
        }
        if (!response.ok) {
            if (response.status === 401) {
                await this.tokens.onUnauthorized?.();
            }
            throw new ApiError(response.status, parsed);
        }
        return parsed;
    }
    // ------------------------------------------------------------------ //
    // V0022 - organization × vertical + location dept overrides
    // ------------------------------------------------------------------ //
    async listOrganizationVerticals(organizationId) {
        return this.request("GET", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/verticals`);
    }
    async addOrganizationVertical(organizationId, body) {
        return this.request("POST", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/verticals`, { body });
    }
    async setOrganizationVerticalDefault(organizationId, verticalId, isDefault) {
        return this.request("PATCH", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/verticals/${encodeURIComponent(verticalId)}`, { body: { is_default: isDefault } });
    }
    async removeOrganizationVertical(organizationId, verticalId) {
        await this.request("DELETE", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/verticals/${encodeURIComponent(verticalId)}`);
    }
    async putOrganizationVerticalDepartments(organizationId, verticalId, departmentIds) {
        return this.request("PUT", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/verticals/${encodeURIComponent(verticalId)}/departments`, { body: { department_ids: departmentIds } });
    }
    async listLocationDepartments(locationId) {
        return this.request("GET", `/admin/v1/locations/${encodeURIComponent(locationId)}/departments`);
    }
    async putLocationDepartments(locationId, departmentIds) {
        return this.request("PUT", `/admin/v1/locations/${encodeURIComponent(locationId)}/departments`, { body: { department_ids: departmentIds } });
    }
    // ---------------------------------------------------------------- //
    // POS users (V0042) - anchored at one partner OR organization.
    // Same email at two anchors creates two pos_user_tbl rows on
    // purpose; pass ``email`` to listPosUsers to find every POS account
    // for a given admin user across anchors.
    // ---------------------------------------------------------------- //
    async listPosUsers(params) {
        const qs = new URLSearchParams();
        if (params?.anchor_kind)
            qs.set("anchor_kind", params.anchor_kind);
        if (params?.anchor_id)
            qs.set("anchor_id", params.anchor_id);
        if (params?.email)
            qs.set("email", params.email);
        if (params?.include_disabled)
            qs.set("include_disabled", "true");
        appendPagination(qs, params);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/pos-users${suffix}`);
    }
    async createPosUser(body) {
        return this.request("POST", "/admin/v1/pos-users", { body });
    }
    async getPosUser(id) {
        return this.request("GET", `/admin/v1/pos-users/${encodeURIComponent(id)}`);
    }
    async disablePosUser(id) {
        return this.request("POST", `/admin/v1/pos-users/${encodeURIComponent(id)}/disable`);
    }
    async enablePosUser(id) {
        return this.request("POST", `/admin/v1/pos-users/${encodeURIComponent(id)}/enable`);
    }
    /** GET /admin/v1/pos-users/{id}/memberships/{membership_id}/picker-departments
     *  - merged dept catalog at the membership's location, shaped exactly
     *  like the cashier-side SalePage picker so the dept_id round-trips. */
    async listMembershipPickerDepartments(posUserId, membershipId) {
        return this.request("GET", `/admin/v1/pos-users/${encodeURIComponent(posUserId)}` +
            `/memberships/${encodeURIComponent(membershipId)}/picker-departments`);
    }
    /** PATCH /admin/v1/pos-users/{id}/memberships/{membership_id}/default-department
     *  - set or clear (pass null) the default department for a cashier's
     *  location membership. Persists to pos_user_membership_tbl with
     *  audit. Mirrors /pos/v1/profile/memberships/{id}/default-department
     *  on the cashier side. */
    async setMembershipDefaultDepartment(posUserId, membershipId, defaultDepartmentId) {
        return this.request("PATCH", `/admin/v1/pos-users/${encodeURIComponent(posUserId)}` +
            `/memberships/${encodeURIComponent(membershipId)}/default-department`, { body: { default_department_id: defaultDepartmentId } });
    }
    // ---------------------------------------------------------------- //
    // /admin/v1/people - unified user-management surface. Merges admin
    // (user_tbl) + pos (pos_user_tbl) populations with a ``pool``
    // discriminator + memberships rollup. Per-pool counts in the
    // response so the UI can render filter chips.
    // ---------------------------------------------------------------- //
    async listPeople(params) {
        const qs = new URLSearchParams();
        if (params?.pool)
            qs.set("pool", params.pool);
        if (params?.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params?.organization_id)
            qs.set("organization_id", params.organization_id);
        if (params?.location_id)
            qs.set("location_id", params.location_id);
        if (params?.role_slug)
            qs.set("role_slug", params.role_slug);
        if (params?.include_disabled)
            qs.set("include_disabled", "true");
        appendPagination(qs, params);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/people${suffix}`);
    }
    /** GET /admin/v1/people/{pool}/{id} - pool-aware detail dispatch.
     *  Returns the same JSON shape as /admin/v1/users/{id} (admin) or
     *  /admin/v1/pos-users/{id} (pos), so callers can keep using the
     *  pool-specific types where they already do. */
    async getPerson(pool, id) {
        return this.request("GET", `/admin/v1/people/${encodeURIComponent(pool)}/${encodeURIComponent(id)}`);
    }
    // ---------------------------------------------------------------- //
    // /admin/v1/grants - pool-pivoting role grant. One body shape writes
    // user_membership_tbl OR pos_user_membership_tbl atomically with
    // audit. Replaces the legacy /admin/v1/users/{id}/roles + initial-
    // membership-on-create paths from the unified UI's perspective.
    // ---------------------------------------------------------------- //
    async createGrant(body) {
        return this.request("POST", "/admin/v1/grants", { body });
    }
    /** DELETE /admin/v1/grants/{grant_id}?pool=admin|pos - admin pool
     *  hard-deletes (matches the legacy /roles revoke); pos pool soft-
     *  revokes by stamping ``revoked_at`` + ``revoked_by_user_id`` so
     *  billing/audit history survives. */
    async revokeGrant(pool, grantId) {
        const suffix = `?pool=${encodeURIComponent(pool)}`;
        return this.request("DELETE", `/admin/v1/grants/${encodeURIComponent(grantId)}${suffix}`);
    }
    // ---------------------------------------------------------------- //
    // Applications + entitlements (V0046 cascade - partner → org → loc)
    // ---------------------------------------------------------------- //
    /** GET /admin/v1/platform/applications - global app catalogue. Open
     *  to any authenticated caller (drives Applications tab + dropdowns). */
    async listPlatformApplications() {
        return this.request("GET", "/admin/v1/platform/applications");
    }
    /** POST /admin/v1/platform/applications - super_admin only. */
    async createPlatformApplication(body) {
        return this.request("POST", "/admin/v1/platform/applications", { body });
    }
    /** PATCH /admin/v1/platform/applications/{slug} - super_admin only. */
    async patchPlatformApplication(slug, body) {
        return this.request("PATCH", `/admin/v1/platform/applications/${encodeURIComponent(slug)}`, { body });
    }
    /** DELETE /admin/v1/platform/applications/{slug} - super_admin only.
     *  Blocked when entitlement rows reference the slug; retire via
     *  PATCH is_active=false instead. */
    async deletePlatformApplication(slug) {
        return this.request("DELETE", `/admin/v1/platform/applications/${encodeURIComponent(slug)}`);
    }
    // Partner-scope entitlements
    async getPartnerEntitlements(partnerId) {
        return this.request("GET", `/admin/v1/partners/${encodeURIComponent(partnerId)}/entitlements`);
    }
    async putPartnerEntitlement(partnerId, appSlug, body) {
        return this.request("PUT", `/admin/v1/partners/${encodeURIComponent(partnerId)}/entitlements/${encodeURIComponent(appSlug)}`, { body });
    }
    async clearPartnerEntitlement(partnerId, appSlug) {
        return this.request("DELETE", `/admin/v1/partners/${encodeURIComponent(partnerId)}/entitlements/${encodeURIComponent(appSlug)}`);
    }
    // Organization-scope entitlements
    async getOrganizationEntitlements(organizationId) {
        return this.request("GET", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/entitlements`);
    }
    async putOrganizationEntitlement(organizationId, appSlug, body) {
        return this.request("PUT", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/entitlements/${encodeURIComponent(appSlug)}`, { body });
    }
    async clearOrganizationEntitlement(organizationId, appSlug) {
        return this.request("DELETE", `/admin/v1/organizations/${encodeURIComponent(organizationId)}/entitlements/${encodeURIComponent(appSlug)}`);
    }
    // Location-scope entitlements
    async getLocationEntitlements(locationId) {
        return this.request("GET", `/admin/v1/locations/${encodeURIComponent(locationId)}/entitlements`);
    }
    async putLocationEntitlement(locationId, appSlug, body) {
        return this.request("PUT", `/admin/v1/locations/${encodeURIComponent(locationId)}/entitlements/${encodeURIComponent(appSlug)}`, { body });
    }
    async clearLocationEntitlement(locationId, appSlug) {
        return this.request("DELETE", `/admin/v1/locations/${encodeURIComponent(locationId)}/entitlements/${encodeURIComponent(appSlug)}`);
    }
    // ---------------------------------------------------------------- //
    // AI Costs (platform super_admin only)
    // ---------------------------------------------------------------- //
    async getAiCostSummary(params) {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/ai-costs/summary${suffix}`);
    }
    // ---------------------------------------------------------------- //
    // M2M API clients (V0095)                                          //
    // ---------------------------------------------------------------- //
    /** GET /admin/v1/partners/{id}/m2m-clients - list active M2M clients. */
    async listM2MClients(partnerId) {
        return this.request("GET", `/admin/v1/partners/${encodeURIComponent(partnerId)}/m2m-clients`);
    }
    /**
     * POST /admin/v1/partners/{id}/m2m-clients - create a new M2M client.
     * Returns client_secret ONCE - store it immediately.
     */
    async createM2MClient(partnerId, body) {
        return this.request("POST", `/admin/v1/partners/${encodeURIComponent(partnerId)}/m2m-clients`, { body });
    }
    /** PATCH /admin/v1/partners/{id}/m2m-clients/{clientId} - update name/description/scopes. */
    async updateM2MClient(partnerId, clientId, body) {
        return this.request("PATCH", `/admin/v1/partners/${encodeURIComponent(partnerId)}/m2m-clients/${encodeURIComponent(clientId)}`, { body });
    }
    /** DELETE /admin/v1/partners/{id}/m2m-clients/{clientId} - revoke. */
    async revokeM2MClient(partnerId, clientId) {
        await this.request("DELETE", `/admin/v1/partners/${encodeURIComponent(partnerId)}/m2m-clients/${encodeURIComponent(clientId)}`);
    }
    /**
     * POST /admin/v1/partners/{id}/m2m-clients/{clientId}/rotate - rotate secret.
     * Returns the new client_id + client_secret ONCE.
     */
    async rotateM2MClient(partnerId, clientId) {
        return this.request("POST", `/admin/v1/partners/${encodeURIComponent(partnerId)}/m2m-clients/${encodeURIComponent(clientId)}/rotate`);
    }
    /**
     * POST /admin/v1/partners/{id}/m2m-clients/{clientId}/test
     * Server-side proxy: exchanges credentials with Cognito, optionally calls
     * the POS API, and returns step-by-step results. Never stores the secret.
     */
    async testM2MClient(partnerId, clientId, body) {
        return this.request("POST", `/admin/v1/partners/${encodeURIComponent(partnerId)}/m2m-clients/${encodeURIComponent(clientId)}/test`, { body });
    }
    // -------------------------------------------------------------------- //
    // Webhook endpoints                                                      //
    // -------------------------------------------------------------------- //
    async listWebhookEndpoints(partnerId) {
        return this.request("GET", `/admin/v1/partners/${encodeURIComponent(partnerId)}/webhook-endpoints`);
    }
    async createWebhookEndpoint(partnerId, body) {
        return this.request("POST", `/admin/v1/partners/${encodeURIComponent(partnerId)}/webhook-endpoints`, { body });
    }
    async disableWebhookEndpoint(partnerId, endpointId) {
        await this.request("DELETE", `/admin/v1/partners/${encodeURIComponent(partnerId)}/webhook-endpoints/${encodeURIComponent(endpointId)}`);
    }
    async listAiCostAudit(params) {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        if (params?.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params?.recipe_kind)
            qs.set("recipe_kind", params.recipe_kind);
        if (params?.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params?.cursor)
            qs.set("cursor", params.cursor);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/ai-costs/audit${suffix}`);
    }
    // ---------------------------------------------------------------- //
    // Platform cost analyzer (super_admin only)
    // ---------------------------------------------------------------- //
    async getCostsSummary(params) {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/costs/summary${suffix}`);
    }
    async getAwsCosts(params) {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        if (params?.granularity)
            qs.set("granularity", params.granularity);
        if (params?.group_by)
            qs.set("group_by", params.group_by);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/costs/aws${suffix}`);
    }
    async getAwsForecast() {
        return this.request("GET", "/admin/v1/platform/costs/aws/forecast");
    }
    async getTagCoverage(params) {
        const qs = new URLSearchParams();
        if (params?.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params?.offset !== undefined)
            qs.set("offset", String(params.offset));
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/costs/aws/tag-coverage${suffix}`);
    }
    async getCostsAiSummary(params) {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/costs/ai/summary${suffix}`);
    }
    async listCostsAiAudit(params) {
        const qs = new URLSearchParams();
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        if (params?.partner_id)
            qs.set("partner_id", params.partner_id);
        if (params?.recipe_kind)
            qs.set("recipe_kind", params.recipe_kind);
        if (params?.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params?.cursor)
            qs.set("cursor", params.cursor);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/costs/ai/audit${suffix}`);
    }
    // ---------------------------------------------------------------- //
    // External API Logs (platform super_admin only)
    // ---------------------------------------------------------------- //
    async listApiLogs(params) {
        const qs = new URLSearchParams();
        if (params?.provider)
            qs.set("provider", params.provider);
        if (params?.http_method)
            qs.set("http_method", params.http_method);
        if (params?.operation)
            qs.set("operation", params.operation);
        if (params?.payment_id)
            qs.set("payment_id", params.payment_id);
        if (params?.order_id)
            qs.set("order_id", params.order_id);
        if (params?.order_ref)
            qs.set("order_ref", params.order_ref);
        if (params?.payment_ref)
            qs.set("payment_ref", params.payment_ref);
        if (params?.location_id)
            qs.set("location_id", params.location_id);
        if (params?.correlation_id)
            qs.set("correlation_id", params.correlation_id);
        if (params?.status_gte !== undefined)
            qs.set("status_gte", String(params.status_gte));
        if (params?.status_lte !== undefined)
            qs.set("status_lte", String(params.status_lte));
        if (params?.has_error !== undefined)
            qs.set("has_error", String(params.has_error));
        if (params?.since)
            qs.set("since", params.since);
        if (params?.until)
            qs.set("until", params.until);
        if (params?.limit !== undefined)
            qs.set("limit", String(params.limit));
        if (params?.cursor)
            qs.set("cursor", params.cursor);
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return this.request("GET", `/admin/v1/platform/api-logs${suffix}`);
    }
    // ---------------------------------------------------------------- //
    // Kiosk devices                                                     //
    // ---------------------------------------------------------------- //
    /** GET /admin/v1/locations/{locId}/kiosks */
    async listKiosks(locId) {
        return this.request("GET", `/admin/v1/locations/${encodeURIComponent(locId)}/kiosks`);
    }
    /** POST /admin/v1/locations/{locId}/kiosks */
    async createKiosk(locId, body) {
        return this.request("POST", `/admin/v1/locations/${encodeURIComponent(locId)}/kiosks`, { body });
    }
    /** PATCH /admin/v1/locations/{locId}/kiosks/{id} */
    async updateKiosk(locId, kioskId, body) {
        return this.request("PATCH", `/admin/v1/locations/${encodeURIComponent(locId)}/kiosks/${encodeURIComponent(kioskId)}`, { body });
    }
    /** POST /admin/v1/locations/{locId}/kiosks/{id}/regenerate-code */
    async regenerateKioskCode(locId, kioskId) {
        return this.request("POST", `/admin/v1/locations/${encodeURIComponent(locId)}/kiosks/${encodeURIComponent(kioskId)}/regenerate-code`);
    }
    /** POST /admin/v1/locations/{locId}/kiosks/{id}/revoke */
    async revokeKiosk(locId, kioskId) {
        return this.request("POST", `/admin/v1/locations/${encodeURIComponent(locId)}/kiosks/${encodeURIComponent(kioskId)}/revoke`);
    }
    /** DELETE /admin/v1/locations/{locId}/kiosks/{id} */
    async deleteKiosk(locId, kioskId) {
        await this.request("DELETE", `/admin/v1/locations/${encodeURIComponent(locId)}/kiosks/${encodeURIComponent(kioskId)}`);
    }
}
//# sourceMappingURL=client.js.map